/**
 * The settled globe on the page: the planet, the backlight moving to the side
 * away from the copy, the shade that dims the city lights under the copy, the
 * network at the global stop, and the places' pins. The heavy layers are drawn
 * on the graphics card by the renderer; this decides what goes into them for a
 * given camera. The sun and crescent themselves are in backlight.ts, and how
 * the globe is sized and turned in globe-look.ts.
 */
import {
  backlightAt,
  CRESCENT,
  CRESCENT_IN,
  DAY_DIM,
  drawSun,
  HOME_LIGHT,
  LIGHT_DEG,
  SUN,
  SUN_IN,
} from './backlight'
import { glow } from './draw'
import { smoothstep } from './easing'
import { RAD } from './data/geo'
import { LAND_IN, METRO_GLOW, R0, TOWN_GLOW } from './globe-look'
import { T_DET } from './intro/supernova'
import { STROKE } from './network'
import type { Network } from './network'
import { projector } from './projection'
import type { Canvas2D, GlobePoint, Light, Renderer, View } from './renderer/renderer'
import type { Viewport } from './viewport'

/* =====================================================================
   TUNING
   ===================================================================== */
/* the light keeps to the side away from the copy at the close-ups, passing behind the planet as it moves */
const AWAY_DEG =
  -152 /* where the light sits when the copy is on the right (deg; LIGHT_DEG mirrored) */
const RING_A = 0.85 /* how bright the rim glows while the sun is straight behind */

/* the shade: the city lights fade across the globe towards the side the copy is on, so the
   copy reads; the planet itself, its land and its atmosphere stay as they are (the look: src/renderer/layers.ts) */
const SHADE_GAP = 40 /* px before the copy's edge where they are already out */

/* the lights */
/**
 * How visible the city lights on the far side of the globe are, 0..1
 * (0 = hidden). Exported so the renderer can use the same value when
 * it shades the lights layer.
 */
export const BACK_A = 0.34
const PIN_GLOW = 1.0 /* the active place's bronze pulse (0 = off) */
const NEAR_BOOST = 1.4 /* how much the lights round the active place brighten */
/* ===================================================================== */

const AWAY_LIGHT: Light = backlightAt(AWAY_DEG * RAD)

/** A place on the globe, as the globe needs to know it. */
export interface Place extends GlobePoint {
  readonly el: Element
  readonly copySide: 'left' | 'right' | undefined
  /** How present the place is, 0..1. Written by the camera as the page scrolls, and by the loop as the globe sinks into the dark. */
  act: number
  /** Where the place is on screen, how big a unit of its surface is there, and how visible it is. Written by the globe when it draws. */
  px: number
  py: number
  kk: number
  vis: number
  /** The copy beside the place. Written by the globe the first time it is needed. */
  box?: Element | null
}

/** What the globe needs to run. */
export interface GlobeOptions {
  /** The 2D canvas to draw the pins, sun and network onto. */
  readonly ctx: Canvas2D
  /** The WebGL renderer that draws the planet, land, towns and city lights. */
  readonly renderer: Renderer
  /** The places on the globe; the globe writes `px`, `py`, `kk`, `vis` and (lazily) `box` onto each when it draws. */
  readonly places: Place[]
  /** The network of undersea-cable arcs, drawn at the global stop. */
  readonly network: Network
}

/**
 * Create the settled globe. Owns the backlight and shade state.
 * When it draws, it writes `px`, `py`, `kk` and `vis` onto each place,
 * and `box` onto each place the first time its shade edge is needed.
 */
export function createGlobe({ ctx, renderer, places, network }: GlobeOptions) {
  /* 0 = the light on its home side, 1 = away; and how far the shade has moved to the copy's side */
  let awayE = 0
  let shadeE = 0
  /* the day side fades as the light passes behind the planet and the crescents swap */
  let dayScale = 1

  /* On the page the light keeps to the side away from the copy. Moving across,
     the sun slips behind the planet and out the other side: one crescent dims
     as the other brightens, and right in between, with the sun straight
     behind, the whole rim lights up as a thin ring. awayE: 0 = home side, 1 = away. */

  return {
    /** The backlight now: on its home side, or away, whichever it is nearer. */
    get light(): Light {
      return awayE < 0.5 ? HOME_LIGHT : AWAY_LIGHT
    },
    /** The light at its home side, as the intro leaves it. */
    reset() {
      awayE = 0
      dayScale = 1
    },
    /**
     * Ease the light over to the side away from the copy, and the shade to the
     * copy's side. `away` and `shade` are the camera's weights; `ho` is how
     * far the hand-over from the intro has come.
     */
    follow(dt: number, away: number, shade: number, ho: number) {
      const k = 1 - Math.exp(-dt * 4)
      awayE += (away * ho - awayE) * k
      shadeE += (shade * ho - shadeE) * k
      dayScale = Math.abs(1 - 2 * awayE)
    },
    /** Draw the settled globe from camera `v`, `netW` being how present the global stop is. */
    draw(vp: Viewport, v: View, t: number, netW: number) {
      const tau = t - T_DET
      const { cx, cy, Rs, d } = v
      const zc = 1 / d
      const proj = projector(v)
      const kC = CRESCENT * smoothstep(CRESCENT_IN[0], CRESCENT_IN[1], tau)
      const kS = SUN * smoothstep(SUN_IN[0], SUN_IN[1], tau)
      const Rref = R0 * vp.min
      const backK = BACK_A * smoothstep(2.4, 6, d)
      const dayK = DAY_DIM * kC * dayScale

      /* the planet: its dark disc, the land, the crescent (on the side away from the copy) */
      const cr = crescents(kC)
      renderer.planet(v, {
        disc: 1,
        land: smoothstep(LAND_IN[0], LAND_IN[1], tau),
        rim: 1,
        lights: cr.lights,
        ring: cr.ring,
      })

      /* every town, the big cities' glow and the lights the supernova set down,
         brighter round the places that are active */
      const near: [Place, number][] = []
      for (const p of places) if (p.act > 0.01) near.push([p, p.act * NEAR_BOOST])
      renderer.setNear(near)
      renderer.setShade(
        vp.mobile ? 0 : shadeE,
        copyEdge(vp, shadeE) - (shadeE >= 0 ? 1 : -1) * SHADE_GAP
      )
      renderer.towns(v, TOWN_GLOW, dayK)
      renderer.metros(v, METRO_GLOW, dayK)
      renderer.lights(v, tau, dayK, backK)

      /* the network, over the whole world */
      network.draw(v, t, netW)

      /* the places: where each one is on screen (for the navigation), and a
         bronze pulse on the one you are at */
      for (const p of places) {
        let A = p.act * PIN_GLOW
        const pt = proj.project(p.x, p.y, p.z)
        const zz = (pt.z - zc) / (1 - zc)
        p.px = pt.x
        p.py = pt.y
        p.kk = pt.kk
        p.vis = smoothstep(0.02, 0.12, zz)
        A *= p.vis
        if (A < 0.004) continue
        const u0 = Math.min(vp.min * 0.012 * Math.sqrt(pt.kk / Rref), vp.min * 0.03)
        glow(ctx, p.px, p.py, u0 * 5, 0.35 * A, 0.15, STROKE)
        for (let ring = 0; ring < 2; ring++) {
          /* two staggered pings */
          const ph = (t / 2.6 + ring * 0.5) % 1
          ctx.strokeStyle = 'rgba(201,164,112,' + (0.75 * A * Math.pow(1 - ph, 2)).toFixed(3) + ')'
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.arc(p.px, p.py, u0 * (1.2 + 5.5 * ph), 0, Math.PI * 2)
          ctx.stroke()
        }
        ctx.fillStyle = 'rgba(' + STROKE + ',' + (0.95 * A).toFixed(3) + ')'
        ctx.beginPath()
        ctx.arc(p.px, p.py, Math.max(u0 * 0.42, 1.6), 0, Math.PI * 2)
        ctx.fill()
      }

      drawSunLit(vp, cx, cy, Rs, kS, t)
    },
  }

  function copyEdge(vp: Viewport, dir: number): number {
    let best: Place | null = null
    let ba = 0
    for (const p of places) {
      if ((p.copySide === 'right') !== dir >= 0 || p.act <= ba) continue
      best = p
      ba = p.act
    }
    if (!best) return dir >= 0 ? vp.width * 0.55 : vp.width * 0.45
    if (!best.box) best.box = best.el.querySelector('.box')
    const r = best.box!.getBoundingClientRect()
    return dir >= 0 ? r.left : r.right
  }

  /** The sun over the crescent(s): both, while the light is moving across. */
  function drawSunLit(
    vp: Viewport,
    cx: number,
    cy: number,
    R: number,
    k: number,
    tw: number
  ): void {
    if (awayE < 0.001) {
      drawSun(ctx, vp, HOME_LIGHT, cx, cy, R, k, tw)
      return
    }
    drawSun(ctx, vp, HOME_LIGHT, cx, cy, R, k * (1 - smoothstep(0, 0.45, awayE)), tw)
    drawSun(ctx, vp, AWAY_LIGHT, cx, cy, R, k * smoothstep(0.55, 1, awayE), tw)
  }

  /* The copy's near edge on screen, for the side the shade is on: the left edge of
     copy on the right, the right edge of copy on the left (of the place most in view). */

  function crescents(k: number): { lights: [number, number][]; ring: number } {
    if (awayE < 0.001 || awayE > 0.999) {
      return { lights: [[(awayE > 0.5 ? AWAY_DEG : LIGHT_DEG) * RAD, k]], ring: 0 }
    }
    const m = awayE
    return {
      lights: [
        [LIGHT_DEG * RAD, k * (1 - smoothstep(0, 0.62, m))],
        [AWAY_DEG * RAD, k * smoothstep(0.38, 1, m)],
      ],
      ring: Math.pow(Math.sin(Math.PI * m), 1.6) * RING_A * k,
    }
  }
}

export type Globe = ReturnType<typeof createGlobe>
